// @flow
import React from 'react';
import {graphql} from '@apollo/react-hoc';
import gql from 'graphql-tag';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {ErrorReport, SuccessReport} from '@homeaway/react-error-reporter';
import seoMetaDataQuery from '../../queries/seoMetaDataQuery';
import {isSearchRoot} from '../../../server/utils/canonical';
import {sanitizeParams} from '../../../server/utils/queryParams';
import {pick} from 'lodash';

import type {ComponentType} from 'react';
import type {SearchRequestQueryResults} from '../../queries/searchRequestQuery';
import type {SearchParamsType} from '../../reducers/searchParams';
import type {State as ReduxState} from '../../reducers';
import type {
    SeoMetaData,
    SeoMetaDataResponseData
} from '../../queries/seoMetaDataQuery';
import {HA_PTA_LANDING_SERP_TO_SLP_PHASE_1} from '../../abacus/experiments';

/**
 * @type{Object}
 * Is using in seoMetaDataQueryOptions function that generate params for seoMetaData query.
 */
type SeoMetaDataParams = {
    searchParams: SearchParamsType,
    results: ?SearchRequestQueryResults,
    pathname: string,
    canonicalSeoLinkExperiment?: boolean
};

type StateProps = {
    searchParams: SearchParamsType,
    pathname: string,
    canonicalSeoLinkExperiment: boolean
};

type InputProps = {
    results: ?SearchRequestQueryResults,
    resultsLoading: boolean
};

type GraphQLProps = {
    seoDataResponse: SeoMetaDataResponseData
};

type ConnectorProps = InputProps & StateProps & GraphQLProps;

/**
 * @type{Object}
 * Type to be used by components applying this HOC. Describes the injected
 * `seoData` prop.
 */
export type WithSeoMetaDataProps = {
    seoData: SeoMetaData,
    canonicalSeoLinkExperiment?: boolean
};

export const fragments = {
    SearchResult: gql`
        fragment SEOMetaDataParamsSearchResult on SearchResult {
            page
            resultCount
            pageSize
            geography {
                name
                lbsId
                breadcrumbs {
                    name
                }
            }
        }
    `
};

const paramsToLog = [
    'isBoundingBoxSearch',
    'legacyRegionPath',
    'pinnedPropertyListing',
    'q',
    'regionId',
    'searchTermUuid'
];

const systemDataToLog = ['isGoodBot', 'siteName', 'domainName'];

// TODO: [TRAVOPS-1135] remove pathname upon adding "referer" to headers whitelist
// See https://jira.homeawaycorp.com/browse/TRAVOPS-1135
const logDataPropWhitelist = [...systemDataToLog, 'pathname'];

const generateSeoMetaDataParam = ({
    searchParams: {
        filter,
        petIncluded,
        currency,
        adultsCount = 0,
        childrenCount = 0,
        maxBathrooms,
        maxBedrooms,
        minBathrooms,
        minBedrooms,
        maxPrice: maxNightlyPrice,
        minPrice: minNightlyPrice
    } = {},
    results
}: SeoMetaDataParams) => {
    const {geography, pageSize, page, resultCount: hitCount} = results || {};
    const {breadcrumbs = [], lbsId: lbsUuid, name: lastBreadcrumbNode} =
        geography || {};
    const parentRegion = (breadcrumbs[breadcrumbs.length - 1] || {}).name;
    return sanitizeParams({
        filter,
        petIncluded,
        currency,
        adultsCount,
        childrenCount,
        maxBathrooms,
        maxBedrooms,
        minBathrooms,
        minBedrooms,
        maxNightlyPrice,
        minNightlyPrice,
        lastBreadcrumbNode,
        parentRegion,
        pageSize,
        page,
        hitCount,
        lbsUuid
    }).result;
};

export const seoMetaDataQueryOptions = (props: SeoMetaDataParams) => ({
    variables: {
        args: generateSeoMetaDataParam(props),
        isSearchRoot: isSearchRoot(props.pathname),
        canonicalSeoLinkExperiment: props.canonicalSeoLinkExperiment
    },
    errorPolicy: 'all'
});

export const mapStateToProps = ({
    abacus,
    system,
    searchParams
}: ReduxState): StateProps => ({
    searchParams: searchParams.params,
    pathname: searchParams.pathname || '',
    canonicalSeoLinkExperiment:
        (abacus[HA_PTA_LANDING_SERP_TO_SLP_PHASE_1] || {}).value === 1,
    ...pick(system, systemDataToLog)
});

function pickRequiredSeoFields(seoMetaData) {
    const {
        pageTitle,
        metaDescription,
        headlineTitle,
        canonicalLink
    } = seoMetaData;
    return {
        pageTitle,
        metaDescription,
        headlineTitle,
        canonicalLink
    };
}

function validateSeoMetaData(seoMetaData) {
    if (!seoMetaData) {
        return ['seoMetaData missing'];
    }
    return Object.keys(pickRequiredSeoFields(seoMetaData))
        .filter((key) => !seoMetaData[key])
        .map((key) => `${key} is missing`);
}

const skip = (props: StateProps & InputProps) =>
    !props.results || props.resultsLoading;

const commonLogData = (props) => ({
    ...pick(props, logDataPropWhitelist),
    ...pick(props.searchParams, paramsToLog)
});

// typed for flow 0.83 via
// https://github.com/facebook/flow/blob/91e85b8457671fe6c781873e37ae317e1402c830/website/en/docs/react/hoc.md
// doesn't matter because the types don't get enforced outside :/
export default function withSeoMetaData<Props: {}>(
    WrappedComponent: ComponentType<Props>
): ComponentType<$Diff<Props, {seoData: SeoMetaData | void}>> {
    const SeoDataComponent = (props: Props & ConnectorProps) => {
        const {seoDataResponse, results, ...rest} = props;
        const {loading, error, seoMetaData} = seoDataResponse || {};

        const seoDataComponent = (
            <WrappedComponent {...rest} seoData={seoMetaData || {}} />
        );

        // Exclude loading state from error reporting
        if (loading || skip(props)) {
            return seoDataComponent;
        }

        if (error) {
            return (
                <ErrorReport
                    name="Failed SEO data fetch"
                    logData={{
                        error: {
                            message: error.message,
                            type: error.constructor.name
                        },
                        ...commonLogData(props)
                    }}
                >
                    {seoDataComponent}
                </ErrorReport>
            );
        }

        const seoMetaDataErrors = validateSeoMetaData(seoMetaData);
        if (seoMetaDataErrors.length) {
            return (
                <ErrorReport
                    name="Missing SEO data"
                    logData={{
                        seoMetaDataErrors,
                        ...commonLogData(props)
                    }}
                >
                    {seoDataComponent}
                </ErrorReport>
            );
        }

        return (
            <SuccessReport name="SEO data fetched">
                {seoDataComponent}
            </SuccessReport>
        );
    };

    return compose(
        connect(mapStateToProps),
        graphql(seoMetaDataQuery, {
            options: ({
                searchParams,
                results,
                pathname,
                canonicalSeoLinkExperiment
            }: StateProps & InputProps) =>
                seoMetaDataQueryOptions({
                    searchParams,
                    results,
                    pathname,
                    canonicalSeoLinkExperiment
                }),
            skip,
            name: 'seoDataResponse'
        })
    )(SeoDataComponent);
}
